import { useEffect, useState } from "react";
import axios from "axios";
import usePageTitle from "@/hooks/use-page-title";
import { Quote, User, Target, Compass, Award, Users, MapPin, Phone, Mail, Calendar, Building2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function About() {
  usePageTitle("About Us");
  const [photos, setPhotos] = useState({});
  useEffect(() => {
    axios.get(`${API}/media?category=founder_aakash`).then((r) => setPhotos((p) => ({ ...p, aakash: r.data[0]?.id }))).catch(() => {});
    axios.get(`${API}/media?category=founder_aashish`).then((r) => setPhotos((p) => ({ ...p, aashish: r.data[0]?.id }))).catch(() => {});
  }, []);

  const founders = [
    {
      key: "aakash",
      name: "Aakash Umesh Hawaldar",
      role: "Co-Founder & Director",
      body: "Aakash leads business development, client relations, sales strategy, and overall operations at Brothers Advertising. His vision is to build long-term partnerships by delivering premium-quality printing and branding solutions backed by exceptional customer service.",
      quote: "Every project is an opportunity to help our clients strengthen their brand and grow their business.",
    },
    {
      key: "aashish",
      name: "Aashish Umesh Hawaldar",
      role: "Co-Founder & Director",
      body: "Aashish oversees production management, quality assurance, and operational excellence. With a strong commitment to precision and craftsmanship, he ensures every product leaving Brothers Advertising meets the highest standards.",
      quote: "Quality isn't just our promise — it's the foundation of everything we create.",
    },
  ];

  const values = [
    { icon: Award, title: "Craftsmanship", desc: "Every job passes through 3 quality gates before it leaves our floor." },
    { icon: Sparkles, title: "Innovation", desc: "From digital ordering to auto-designed templates — we lean into tech." },
    { icon: Users, title: "Partnership", desc: "9 out of 10 clients come back. Long-term relationships > one-time deals." },
    { icon: Target, title: "Reliability", desc: "On-time delivery, transparent pricing, GST-compliant invoicing." },
  ];

  const stats = [
    { v: "20K+", k: "Orders Delivered" },
    { v: "10L+", k: "Prints Produced" },
    { v: "6+ Yrs", k: "Serving Clients" },
    { v: "4.9★", k: "Customer Rating" },
  ];

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-orange-50 via-white to-white overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-[0.04] bg-[linear-gradient(#000_1px,transparent_1px),linear-gradient(90deg,#000_1px,transparent_1px)] bg-[size:44px_44px]" />
        <div className="max-w-5xl mx-auto px-4 lg:px-8 text-center">
          <div className="overline mb-3 text-[#FF6B00]">About Brothers Advertising</div>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.95]">
            Ink, ideas &<br /><span className="text-[#FF6B00]">a family legacy.</span>
          </h1>
          <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 border border-zinc-300 px-3 py-1.5 rounded-full" data-testid="parent-company-badge">
            <span className="w-1.5 h-1.5 bg-[#FF6B00] rounded-full" /> A unit of <span className="font-bold text-zinc-900">J U ENTERPRISES</span>
          </div>
          <p className="mt-6 text-lg text-zinc-600 max-w-3xl mx-auto leading-relaxed">
            Founded by two brothers who turned a passion for print into a trusted business serving individuals, startups, corporates, educational institutions and government clients across India. What started as a two-man dream is today one of Navi Mumbai's most reliable print & advertising partners.
          </p>
        </div>
      </section>

      {/* Stats strip */}
      <section className="py-12 bg-zinc-900 text-white">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {stats.map((s) => (
            <div key={s.k}>
              <div className="font-display font-black text-4xl md:text-5xl text-[#FF6B00]">{s.v}</div>
              <div className="text-xs uppercase tracking-widest text-zinc-400 mt-1">{s.k}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Vision + Mission (side-by-side cards) */}
      <section className="py-20 max-w-6xl mx-auto px-4 lg:px-8">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="relative p-8 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-700 text-white overflow-hidden group hover:shadow-2xl transition-shadow" data-testid="vision-card">
            <div className="absolute -top-6 -right-6 opacity-10 group-hover:opacity-20 transition-opacity">
              <Compass size={180} />
            </div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold mb-4 border border-white/40 px-3 py-1 rounded-full">
                <Compass size={12} /> Vision
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter leading-tight">To be India's most trusted print & branding partner for growing businesses.</h2>
              <p className="mt-4 text-white/90 leading-relaxed">
                We believe every business — from a solo entrepreneur to a listed enterprise — deserves world-class print quality without the corporate red tape. Our vision is a future where every SME can print, brand and market as boldly as a Fortune 500.
              </p>
            </div>
          </div>

          <div className="relative p-8 rounded-2xl bg-zinc-900 text-white overflow-hidden group hover:shadow-2xl transition-shadow" data-testid="mission-card">
            <div className="absolute -top-6 -right-6 opacity-10 group-hover:opacity-20 transition-opacity">
              <Target size={180} />
            </div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold mb-4 border border-[#FF6B00] text-[#FF6B00] px-3 py-1 rounded-full">
                <Target size={12} /> Mission
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter leading-tight">Deliver premium prints — on time, on brand, on budget. Every single time.</h2>
              <ul className="mt-4 space-y-2 text-white/90 leading-relaxed text-sm">
                <li className="flex gap-2"><span className="text-[#FF6B00] font-black">→</span> Combine traditional craftsmanship with modern digital workflows.</li>
                <li className="flex gap-2"><span className="text-[#FF6B00] font-black">→</span> Give every client a dedicated point-of-contact — no ticket rot.</li>
                <li className="flex gap-2"><span className="text-[#FF6B00] font-black">→</span> Ship with strict quality gates and GST-compliant invoicing.</li>
                <li className="flex gap-2"><span className="text-[#FF6B00] font-black">→</span> Reinvest in machines, people and technology every year.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <div className="overline mb-2 text-[#FF6B00]">What We Stand For</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Four values.<br />One promise.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((v) => (
              <div key={v.title} className="p-6 bg-white border border-zinc-200 rounded-2xl hover:border-[#FF6B00] hover:-translate-y-1 transition-all" data-testid={`value-${v.title.toLowerCase()}`}>
                <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-4">
                  <v.icon size={20} className="text-[#FF6B00]" />
                </div>
                <div className="font-display font-black text-lg tracking-tight">{v.title}</div>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="py-20 max-w-6xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="overline mb-2 text-[#FF6B00]">Meet the Founders</div>
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Two brothers.<br />One printing press.</h2>
        </div>
        <div className="space-y-6">
          {founders.map((f) => (
            <div key={f.name} className="grid md:grid-cols-3 gap-0 bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="bg-gradient-to-br from-zinc-900 to-zinc-700 text-white p-8 flex flex-col items-center justify-center text-center">
                {photos[f.key] ? (
                  <img src={`${API}/media/${photos[f.key]}`} alt={f.name} className="w-32 h-32 rounded-full object-cover border-2 border-[#FF6B00] mb-4" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-white/10 border-2 border-[#FF6B00] flex items-center justify-center mb-4">
                    <User size={40} className="text-[#FF6B00]" />
                  </div>
                )}
                <div className="font-display font-black text-lg">{f.name}</div>
                <div className="text-xs uppercase tracking-widest text-[#FF6B00] font-bold mt-1">{f.role}</div>
              </div>
              <div className="md:col-span-2 p-8 space-y-4">
                <p className="text-zinc-700 leading-relaxed">{f.body}</p>
                <div className="border-l-4 border-[#FF6B00] pl-4 py-2 bg-orange-50/50">
                  <Quote size={16} className="text-[#FF6B00] mb-1" />
                  <p className="font-display italic text-lg text-zinc-800">"{f.quote}"</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Company Details */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <div className="overline mb-2 text-[#FF6B00]">Company Details</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter">The essentials.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Building2, k: "Legal Name", v: "J U Enterprises" },
              { icon: Sparkles, k: "Brand", v: "Brothers Advertising" },
              { icon: Calendar, k: "Established", v: "2019" },
              { icon: MapPin, k: "Head Office", v: "Navi Mumbai, Maharashtra, India" },
              { icon: Phone, k: "Customer Care", v: "022 4742 7059" },
              { icon: Mail, k: "Email", v: "print@brothersdigital.in" },
              { icon: Award, k: "GSTIN", v: "27APDPH3761H1ZY" },
              { icon: Users, k: "Team Size", v: "12+ specialists" },
              { icon: Target, k: "Service Areas", v: "PAN India (delivery via Blue Dart, DTDC & self-fleet)" },
            ].map((it) => (
              <div key={it.k} className="p-5 bg-white border border-zinc-200 rounded-2xl flex items-start gap-3" data-testid={`detail-${it.k.replace(/\s/g, "-").toLowerCase()}`}>
                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <it.icon size={16} className="text-[#FF6B00]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{it.k}</div>
                  <div className="text-sm font-semibold text-zinc-900 mt-0.5 break-words">{it.v}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-20 bg-zinc-900 text-white">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-[#FF6B00] font-bold mb-4">Our Shared Vision</div>
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter mb-6">
            One goal. Two brothers.<br />Zero compromise on quality.
          </h2>
          <p className="text-lg text-zinc-300 leading-relaxed">
            Together, Aakash and Aashish Hawaldar are building Brothers Advertising into one of India's most trusted printing and advertising companies — combining creativity, advanced technology, superior quality, and outstanding customer service.
          </p>
        </div>
      </section>
    </div>
  );
}
